'use client';

import { useRef, useEffect } from 'react';
import { MapPin, Calendar } from 'lucide-react';
import type { ChurchEvent, ContentSlide, CardTheme } from '@/types/serve-finder';
import { Img, useAssetUrl, useBgUrl } from '@/lib/asset-url';
import { EventDetailContent } from './EventDetailContent';
import { isLightColor } from './EventHelpers';

interface FeaturedEventSectionProps {
  event: ChurchEvent;
  reversed?: boolean;
}

// ---------------------------------------------------------------------------
// Marquee slide card — compact card matching EventContentSlider visuals
// ---------------------------------------------------------------------------

function MarqueeSlideCard({
  slide,
  theme,
  personOrdinal,
}: {
  slide: ContentSlide;
  theme: CardTheme | null;
  personOrdinal: number;
}) {
  const resolve = useAssetUrl();
  const lightBG = !!(slide.BG_Color && isLightColor(slide.BG_Color));

  const themeHeadingClashesBG =
    theme?.Title_Color &&
    slide.BG_Color &&
    theme.Title_Color.toLowerCase().slice(0, 4) === slide.BG_Color.toLowerCase().slice(0, 4);
  const labelColor = lightBG
    ? 'rgba(0,0,0,0.5)'
    : (theme?.Subtitle_Color ?? 'rgba(255,255,255,0.6)');
  const headingColor = themeHeadingClashesBG
    ? '#2fbfaf'
    : (theme?.Title_Color ?? (lightBG ? '#1a1a1a' : '#ffffff'));
  const blurbColor = lightBG ? 'rgba(0,0,0,0.6)' : (theme?.Body_Color ?? 'rgba(255,255,255,0.8)');

  const isTagline = !slide.Label && !slide.Blurb;
  const isPerson = !!slide.Image_URL;
  const imageOnLeft = isPerson && personOrdinal % 2 === 1;

  return (
    <div
      className="relative flex shrink-0 overflow-hidden"
      style={{
        width: 340,
        height: 100,
        backgroundColor: slide.BG_Color ?? undefined,
      }}
    >
      {slide.BG_Image && (
        <Img src={slide.BG_Image} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}

      {slide.BG_Color && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${resolve('/Hope-365/circles-stars-white.png')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: lightBG ? 0.08 : 0.15,
          }}
        />
      )}

      {isPerson && (
        <div
          className={`absolute top-2 bottom-2 flex w-[42%] items-center justify-center ${
            imageOnLeft ? 'left-2' : 'right-2'
          }`}
        >
          <Img src={slide.Image_URL!} alt="" className="h-full w-full object-contain" />
        </div>
      )}

      <div
        className={`relative z-10 flex flex-1 flex-col px-4 py-2.5 ${
          isTagline ? 'items-center justify-center text-center' : 'justify-center'
        } ${isPerson ? (imageOnLeft ? 'items-end text-right' : '') : ''}`}
      >
        {slide.Label && (
          <p
            className="text-[10px] font-light tracking-[0.2em] uppercase"
            style={{ color: labelColor }}
          >
            {slide.Label}
          </p>
        )}
        <h3
          className={`leading-[0.9] font-black whitespace-pre-line uppercase ${
            isTagline
              ? 'text-2xl -tracking-[0.09em]'
              : isPerson
                ? 'mt-1 text-xl -tracking-[0.09em]'
                : 'mt-1 text-lg -tracking-[0.09em]'
          }`}
          style={{ color: headingColor }}
        >
          {slide.Heading}
        </h3>
        {slide.Blurb && !isPerson && slide.Blurb.includes('|') ? (
          <div className="mt-1.5 flex gap-2">
            {slide.Blurb.split('|').map((part, i) => (
              <div
                key={i}
                className={`text-[10px] font-semibold tracking-wide uppercase ${i > 0 ? 'border-l border-white/25 pl-2' : ''}`}
                style={{ color: blurbColor }}
              >
                {part.trim()}
              </div>
            ))}
          </div>
        ) : slide.Blurb ? (
          <p
            className={`mt-1 leading-[0.95] ${
              isPerson ? 'text-sm font-black -tracking-[0.09em] uppercase' : 'text-xs'
            }`}
            style={{
              color: isPerson ? (lightBG ? '#8b35c4' : 'rgba(255,255,255,0.85)') : blurbColor,
            }}
          >
            {slide.Blurb}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Continuous marquee strip
// ---------------------------------------------------------------------------

function FeaturedSlideMarquee({
  slides,
  theme,
}: {
  slides: ContentSlide[];
  theme: CardTheme | null;
}) {
  // Pre-compute person ordinals across the full set
  const personSlideIndices = slides.map((s, i) => (s.Image_URL ? i : -1)).filter((i) => i >= 0);

  const renderSlides = (keyPrefix: string) =>
    slides.map((slide, i) => (
      <MarqueeSlideCard
        key={`${keyPrefix}-${i}`}
        slide={slide}
        theme={theme}
        personOrdinal={personSlideIndices.indexOf(i)}
      />
    ));

  // Speed: ~30s for one full set (scales with number of slides)
  const duration = Math.max(slides.length * 13, 40);

  return (
    <div
      className="group overflow-hidden border-t"
      style={{ borderColor: theme?.Border_Color ?? 'var(--border)' }}
    >
      <div
        className="flex gap-8 px-3 py-3 group-hover:[animation-play-state:paused]"
        style={{
          animation: `featured-marquee ${duration}s linear infinite`,
          width: 'max-content',
        }}
      >
        {renderSlides('a')}
        {renderSlides('b')}
      </div>
      <style>{`
        @keyframes featured-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Featured Event Section
// ---------------------------------------------------------------------------

export function FeaturedEventSection({ event, reversed }: FeaturedEventSectionProps) {
  const resolve = useAssetUrl();
  const bgUrlFn = useBgUrl();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  const theme = event.Card_Theme;
  const dateDisplay = event.End_Date ? `${event.Start_Date} – ${event.End_Date}` : event.Start_Date;

  const totalVolunteersNeeded = event.Serving_Roles.reduce(
    (sum, r) => sum + (r.Volunteers_Needed ?? 0),
    0
  );

  const hasServing = event.Serving_Roles.length > 0;
  const hasSessions = event.Sessions.length > 0;
  const hasContentSlides = event.Content_Slides != null && event.Content_Slides.length > 0;
  const hasSmartReg = event.Smart_Registration != null;
  const hasOptions = event.Registration_Options != null && event.Registration_Options.length > 0;
  const hasServiceTimes =
    hasSessions && event.Sessions.some((s) => s.Service_Times && s.Service_Times.length > 0);
  const firstSessionWithTimes = hasServiceTimes
    ? event.Sessions.find((s) => s.Service_Times && s.Service_Times.length > 0)
    : null;

  const hasFullCardTheme = !!(theme?.Body_BG_Image && event.BG_URL === theme.Body_BG_Image);
  const hasThemedBodyFooter = !!(theme?.Body_BG_Image && !hasFullCardTheme);

  // In featured mode the BG image spans the entire section, so don't repeat it on the body
  const bodyStyle: React.CSSProperties = {};

  const sectionBg: React.CSSProperties = hasFullCardTheme
    ? {
        backgroundColor: theme!.Body_BG,
        backgroundImage: bgUrlFn(theme!.Body_BG_Image!),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : hasThemedBodyFooter
      ? {
          backgroundColor: theme!.Body_BG,
          backgroundImage: bgUrlFn(theme!.Body_BG_Image!),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : theme
        ? { backgroundColor: theme.Body_BG }
        : {};

  return (
    <div className="overflow-hidden shadow-lg" style={sectionBg}>
      <div
        className="grid grid-cols-[45fr_55fr]"
        style={reversed ? { direction: 'rtl' } : undefined}
      >
        {/* Hero column */}
        <div className="relative flex flex-col overflow-hidden" style={{ direction: 'ltr' }}>
          {/* BG media + FG image */}
          <div className="relative flex-1">
            {event.BG_Is_Video ? (
              <>
                <video
                  ref={videoRef}
                  src={resolve(event.BG_URL)}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />
                {event.FG_URL && (
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    <Img
                      src={event.FG_URL}
                      alt={event.Title}
                      className="max-h-[60%] max-w-[80%] object-contain drop-shadow-lg"
                    />
                  </div>
                )}
              </>
            ) : hasFullCardTheme ? (
              <div className="flex h-full flex-col items-center justify-center px-8 py-6">
                {event.FG_URL && (
                  <Img
                    src={event.FG_URL}
                    alt={event.Title}
                    className="max-h-[50%] max-w-[80%] object-contain drop-shadow-lg"
                  />
                )}
                {hasServiceTimes && firstSessionWithTimes && (
                  <div className="mt-4 flex items-center gap-2.5">
                    <Img
                      src={`/${event.BG_URL.split('/')[1]}/${firstSessionWithTimes.Label.replace(/\s+/g, '')}.svg`}
                      alt=""
                      className="h-8 w-8"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span
                      className="text-sm font-bold tracking-wide uppercase"
                      style={{ color: theme?.Title_Color ?? '#ffffff' }}
                    >
                      {firstSessionWithTimes.Label}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Img src={event.BG_URL} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />
                {event.FG_URL && (
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    <Img
                      src={event.FG_URL}
                      alt={event.Title}
                      className="max-h-[60%] max-w-[80%] object-contain drop-shadow-lg"
                    />
                  </div>
                )}
              </>
            )}

            {/* Date + campus strip at bottom of hero area */}
            <div
              className={`absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 px-4 pt-4 pb-3 text-[11px] font-semibold tracking-widest uppercase ${hasFullCardTheme ? '' : 'bg-gradient-to-t from-black/50 to-transparent'}`}
              style={{ color: theme?.Subtitle_Color ?? 'rgba(255,255,255,0.8)' }}
            >
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {event.Congregation_Name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {dateDisplay}
              </span>
            </div>
          </div>
        </div>

        {/* Content column */}
        <div className="flex flex-col overflow-y-auto" style={{ direction: 'ltr' }}>
          {/* Detail content */}
          <EventDetailContent
            event={event}
            theme={theme}
            hasFullCardTheme={hasFullCardTheme}
            bodyStyle={bodyStyle}
            hasServing={hasServing}
            hasSessions={hasSessions}
            hasSmartReg={hasSmartReg}
            hasOptions={hasOptions}
            hasServiceTimes={hasServiceTimes}
            firstSessionWithTimes={firstSessionWithTimes}
            totalVolunteersNeeded={totalVolunteersNeeded}
          />
        </div>
      </div>

      {/* Full-width continuous slide marquee */}
      {hasContentSlides && <FeaturedSlideMarquee slides={event.Content_Slides!} theme={theme} />}
    </div>
  );
}
